import { supabase } from '@/lib/supabaseClient';

/**
 * Settings stored as singleton rows in the `settings` table:
 *   - key='company_profile' — company info (name, phone, email, address, etc.)
 *   - key='crew_rates' — list of crew + hourly rate
 *   - key='defaults' — default markup, payment schedule
 *   - key='notifications' — notification preferences
 *
 * Schema of the `settings` table:
 *   id uuid, key text unique, value jsonb, updated_at timestamp
 */

export const SETTINGS_KEYS = {
  COMPANY: 'company_profile',
  CREW_RATES: 'crew_rates',
  DEFAULTS: 'defaults',
  NOTIFICATIONS: 'notifications',
  GETTING_STARTED: 'getting_started',
};

// Factory defaults are NEUTRAL on purpose. Real company/crew data must never
// be hardcoded here: every new tenant renders these defaults until they save
// their own, so a real name or wage in this file shows up in EVERY new
// account (live incident 2026-09-25: a founding member's fresh account
// displayed the developer's own crew and wages as its starting roster).
export const DEFAULT_COMPANY = {
  name: '',
  legal_name: '',
  phone: '',
  email: '',
  address: '',
  website: '',
  license: '',
  insurance: '',
  tagline: '',
  // Reusable-clause defaults (Phase 26 step 2). Pre-filled into new estimates
  // at creation time; editing an estimate's copy does NOT mutate these.
  default_warranty: '',
  default_special_clauses: '',
  default_change_order_clause: '',
  // White-label proposal branding (tier-gated in the settings UI). See
  // lib/branding.js. Neutral defaults keep the proposal looking unchanged
  // until the company opts in.
  branding: {
    accent_color: '',
    header_font: 'brand',
    logo_placement: 'left',
  },
};

// Empty on purpose — see the note above DEFAULT_COMPANY. A new tenant adds
// their own crew in Settings → Crew Rates; the roster surfaces all handle an
// empty list.
export const DEFAULT_CREW_RATES = [];

export const DEFAULT_DEFAULTS = {
  markup_pct: 25,
  pay_schedule: '40/30/30',
  pay_schedule_options: ['40/30/30', '50/25/25', '40/40/20', '33/33/34', 'Net 30'],
  default_tax_pct: 0,
  default_invoice_due_days: 14,
  // Auto-applied expected-rebate rate per store, in PERCENT (11 = 11%).
  // Menards' 11% is a real standing promo (pre-tax subtotal, mail-in).
  // Lowe's/Home Depot have NO automatic per-receipt rebate — their percents
  // default to 0 and should only be set by contractors actually enrolled in a
  // program (Lowe's card 5%, HD Pro Xtra rebate 2%, etc.). A printed rebate
  // amount scanned off a receipt always overrides the rate.
  store_rebates: { Menards: 11, Lowes: 0, 'Home Depot': 0, Other: 0 },
  // Home Depot Pro Xtra rebate tracker (opt-in). The Pro Xtra rebate pays a
  // % back on qualifying PRE-TAX purchases once net spend clears a minimum
  // within the rebate period (commonly $12,500 semi-annual or $10,000 annual,
  // program-dependent). The tracker shows progress toward that threshold from
  // scanned Home Depot receipts.
  pro_xtra: { enabled: false, threshold_usd: 12500, period: 'semiannual' },
};

export const DEFAULT_NOTIFICATIONS = {
  email_invoice_paid: true,
  email_estimate_signed: true,
  email_overdue_summary: true,
  push_clock_reminders: true,
  daily_recap_time: '17:00',
};

/* ----------------------------------------------------------------------------
 * Generic key-value helpers (with localStorage fallback if RLS denies access)
 * ------------------------------------------------------------------------- */
const LS_PREFIX = 'kocore.settings.';

/**
 * Distinguish an authorization failure (RLS / not-signed-in) from a transient
 * network/offline error. We only want the localStorage cache to stand in for
 * the DB when the DB was *unreachable* — NOT when the DB reachably told us
 * "you are not allowed to read/write this." Masking a permission denial with a
 * stale cache makes a crew_lead's blocked writes look like they succeeded and
 * can serve another user's stale admin settings from this device.
 *
 * Postgres/PostgREST auth signals:
 *   42501 = insufficient_privilege (RLS denied)
 *   PGRST301 / 401 / 403 = JWT missing/expired or unauthorized
 */
const isPermissionError = (error) => {
  if (!error) return false;
  const code = String(error.code || '');
  const status = Number(error.status || error.statusCode || 0);
  const msg = (error.message || '').toLowerCase();
  return (
    code === '42501' ||
    code === 'PGRST301' ||
    status === 401 ||
    status === 403 ||
    msg.includes('permission denied') ||
    msg.includes('row-level security') ||
    msg.includes('not authorized') ||
    msg.includes('jwt')
  );
};

export const fetchSettingsByKey = async (key, fallback = null) => {
  try {
    const { data, error } = await supabase
      .from('settings')
      .select('id,key,value,updated_at')
      .eq('key', key)
      .maybeSingle();
    if (error) {
      // A reachable "you're not allowed" answer must NOT be masked by cache —
      // surface it so callers can react (re-auth / show an error) instead of
      // silently trusting whatever is cached on this device.
      if (isPermissionError(error)) {
        const err = new Error(error.message || 'permission_denied');
        err.code = error.code;
        err.permissionDenied = true;
        throw err;
      }
      // Otherwise treat it as an offline/transient read — fall back to cache.
      const cached = localStorage.getItem(LS_PREFIX + key);
      if (cached) {
        try {
          return { value: JSON.parse(cached), source: 'local' };
        } catch (e) {
          /* noop */
        }
      }
      return { value: fallback, source: 'fallback' };
    }
    if (!data) return { value: fallback, source: 'fallback' };
    // Cache locally for offline/quick reads.
    try {
      localStorage.setItem(LS_PREFIX + key, JSON.stringify(data.value));
    } catch (e) { /* noop */ }
    return { value: data.value, source: 'remote', updated_at: data.updated_at };
  } catch (e) {
    // Re-throw genuine permission failures; only swallow network/parse errors.
    if (e?.permissionDenied || isPermissionError(e)) throw e;
    const cached = localStorage.getItem(LS_PREFIX + key);
    if (cached) {
      try {
        return { value: JSON.parse(cached), source: 'local' };
      } catch (err) {
        /* noop */
      }
    }
    return { value: fallback, source: 'fallback' };
  }
};

export const upsertSettingsByKey = async (key, value) => {
  // Cache locally for offline/quick reads. NOTE: this is a read-through cache,
  // not a durable write buffer — a caller whose write is denied by RLS still
  // gets a thrown error below, so callers must not treat a local cache write
  // as a successful save.
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(value));
  } catch (e) { /* noop */ }

  // Multi-tenant (migration 036): settings are unique per (contractor_id, key),
  // not globally per key. An admin's tenant == their own auth uid (profiles are
  // self-tenant for admins), so we stamp contractor_id from the session and
  // upsert on the composite key. The BEFORE INSERT trigger also stamps it, but
  // sending it explicitly makes the ON CONFLICT target resolve correctly.
  const { data: sessionData } = await supabase.auth.getSession();
  const contractorId = sessionData?.session?.user?.id || null;

  const payload = {
    key,
    value,
    updated_at: new Date().toISOString(),
    ...(contractorId ? { contractor_id: contractorId } : {}),
  };

  // Native upsert with onConflict on the composite unique (contractor_id,key),
  // avoiding races where two callers both INSERT in parallel.
  const { data, error } = await supabase
    .from('settings')
    .upsert(payload, { onConflict: 'contractor_id,key' })
    .select()
    .single();
  if (error) {
    // Drop the optimistic local cache entry on a permission denial so we don't
    // leave a stale value that a later read might serve as if it were saved.
    if (isPermissionError(error)) {
      try {
        localStorage.removeItem(LS_PREFIX + key);
      } catch (e) { /* noop */ }
      const err = new Error(error.message || 'permission_denied');
      err.code = error.code;
      err.permissionDenied = true;
      throw err;
    }
    throw error;
  }
  return data;
};

/* ----------------------------------------------------------------------------
 * Convenience loaders
 * ------------------------------------------------------------------------- */
export const loadAllSettings = async () => {
  const [company, crew, defaults, notifications] = await Promise.all([
    fetchSettingsByKey(SETTINGS_KEYS.COMPANY, DEFAULT_COMPANY),
    fetchSettingsByKey(SETTINGS_KEYS.CREW_RATES, DEFAULT_CREW_RATES),
    fetchSettingsByKey(SETTINGS_KEYS.DEFAULTS, DEFAULT_DEFAULTS),
    fetchSettingsByKey(SETTINGS_KEYS.NOTIFICATIONS, DEFAULT_NOTIFICATIONS),
  ]);
  return {
    company: { ...DEFAULT_COMPANY, ...(company.value || {}) },
    crew: Array.isArray(crew.value) && crew.value.length
      ? crew.value
      : DEFAULT_CREW_RATES,
    defaults: { ...DEFAULT_DEFAULTS, ...(defaults.value || {}) },
    notifications: { ...DEFAULT_NOTIFICATIONS, ...(notifications.value || {}) },
  };
};
